/**
 * /account — Customer self-service hub.
 * Tabs: Profile / My Bookings / My Activity
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  User, Calendar, Activity, LogOut, Mail, Phone, MapPin, Edit2, CheckCircle2,
  Loader2, ArrowLeft, FileText, Receipt, Eye, EyeOff, Shield, ShieldCheck, AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCustomerAuth, formatApiError } from '../contexts/CustomerAuthContext';
import { AvatarUploader, ChangePasswordForm, ChangeContactForm, LoginHistoryTab, TicketsTab } from '../components/CustomerPortalV2';
import CustomerInvoicesTab from '../components/CustomerInvoicesTab';
import CustomerWarrantiesTab from '../components/CustomerWarrantiesTab';
import CustomerServiceTicketsTab from '../components/CustomerServiceTicketsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customers`;

const fmtDate = (s) => s ? new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const EVENT_LABELS = {
  signup: '🎉 Account created',
  login: '🔐 Logged in',
  page_view: '📄 Visited page',
  calculator_open: '🧮 Opened calculator',
  calculator_open_gated: '🔒 Calculator (signup-gated)',
  pest_select: '🐛 Selected pest',
  size_select: '🏠 Selected property size',
  lead_submit: '📨 Submitted lead',
  booking_create: '✅ Created booking',
  payment_success: '💳 Payment successful',
  password_reset: '🔑 Reset password',
  profile_update: '✏️ Updated profile',
  chat_message: '💬 Chatted with AI',
  coupon_apply: '🏷️ Applied coupon',
};

export default function CustomerAccount({ onOpenAuth }) {
  const { customer, isLoggedIn, logout, token, loadingMe } = useCustomerAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('profile');

  if (loadingMe) {
    return <FullPageSpinner />;
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Shield size={40} className="mx-auto text-emerald-600 mb-3" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Login required</h1>
          <p className="text-slate-600 mb-4">Please log in to view your EntoRid account, bookings, and activity.</p>
          <div className="flex gap-2">
            <Button onClick={() => onOpenAuth?.('login')} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="account-need-login">Log in</Button>
            <Button variant="outline" onClick={() => onOpenAuth?.('signup')} className="flex-1" data-testid="account-need-signup">Sign up</Button>
          </div>
          <Link to="/" className="block mt-4 text-sm text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1 justify-center"><ArrowLeft size={14} /> Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 backdrop-blur-md bg-white/90">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-700 hover:text-slate-900 font-bold">
            <ArrowLeft size={16} /> <span className="hidden sm:inline">EntoRid</span>
          </Link>
          <div className="text-sm text-slate-600 hidden sm:block">Hi, <span className="font-semibold text-slate-900">{customer.name?.split(' ')[0]}</span></div>
          <Button variant="outline" size="sm" onClick={() => { logout(); navigate('/'); }} data-testid="account-logout">
            <LogOut size={14} className="mr-1.5" /> Log out
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex gap-1 mb-5 border-b border-slate-200 overflow-x-auto">
          {[
            { id: 'profile', label: 'Profile', Icon: User },
            { id: 'bookings', label: 'My Bookings', Icon: Calendar },
            { id: 'invoices', label: 'My Invoices', Icon: Receipt },
            { id: 'warranties', label: 'Warranties', Icon: ShieldCheck },
            { id: 'complaints', label: 'Complaints', Icon: AlertCircle },
            { id: 'tickets', label: 'Support', Icon: FileText },
            { id: 'activity', label: 'Activity', Icon: Activity },
            { id: 'security', label: 'Security', Icon: Shield },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
                tab === t.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`account-tab-${t.id}`}
            >
              <t.Icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'profile' && <ProfileSection token={token} />}
        {tab === 'bookings' && <BookingsSection token={token} />}
        {tab === 'invoices' && <CustomerInvoicesTab />}
        {tab === 'warranties' && <CustomerWarrantiesTab />}
        {tab === 'complaints' && <CustomerServiceTicketsTab />}
        {tab === 'tickets' && <TicketsTab />}
        {tab === 'activity' && <ActivitySection token={token} />}
        {tab === 'security' && (
          <div className="space-y-5" data-testid="account-security">
            <ChangePasswordForm />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <ChangeContactForm kind="email" />
              <ChangeContactForm kind="mobile" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2"><Shield size={16} /> Login history</h3>
              <LoginHistoryTab />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-400">
      <Loader2 size={28} className="animate-spin" />
    </div>
  );
}

// ============================================================
// PROFILE
// ============================================================
function ProfileSection({ token }) {
  const { customer, updateProfile } = useCustomerAuth();
  const [form, setForm] = useState({ name: '', city: '', address: '' });
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (customer) setForm({
      name: customer.name || '',
      city: customer.city || '',
      address: customer.address || '',
    });
  }, [customer]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(''); setMsg('');
    try {
      await updateProfile(form);
      setMsg('Profile updated');
      setEditing(false);
      setTimeout(() => setMsg(''), 2500);
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  if (!customer) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="account-profile">
      <AvatarUploader />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Profile</h2>
          <p className="text-sm text-slate-500">Manage your personal details</p>
        </div>
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="profile-edit-btn">
            <Edit2 size={14} className="mr-1.5" /> Edit
          </Button>
        )}
      </div>
      {msg && (
        <div className="mb-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3 inline-flex items-center gap-2">
          <CheckCircle2 size={16} /> {msg}
        </div>
      )}
      {err && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}

      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ReadField label="Full Name" value={form.name} editing={editing} onChange={(v) => setForm({ ...form, name: v })} testid="profile-name" />
        <ReadField label="City" value={form.city} editing={editing} onChange={(v) => setForm({ ...form, city: v })} testid="profile-city" placeholder="e.g. Dehradun" />
        <ReadField label="Mobile" value={`+91 ${customer.mobile}`} editing={false} Icon={Phone} />
        <ReadField label="Email" value={customer.email} editing={false} Icon={Mail} />
        <ReadField label="Address" value={form.address} editing={editing} onChange={(v) => setForm({ ...form, address: v })} Icon={MapPin} className="sm:col-span-2" testid="profile-address" placeholder="Street, area, landmark…" />
        {editing && (
          <div className="sm:col-span-2 flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="profile-save-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : 'Save changes'}
            </Button>
          </div>
        )}
      </form>

      <div className="mt-6 pt-5 border-t border-slate-100 grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs text-slate-500">Member since</div>
          <div className="font-semibold text-slate-900">{fmtDate(customer.created_at)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Last login</div>
          <div className="font-semibold text-slate-900">{fmtDate(customer.last_login_at)}</div>
        </div>
      </div>
    </div>
  );
}

function ReadField({ label, value, editing, onChange, Icon, className = '', testid, placeholder }) {
  return (
    <div className={className}>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      {editing && onChange ? (
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="mt-1 h-10" data-testid={testid} placeholder={placeholder} />
      ) : (
        <div className="mt-1 h-10 flex items-center gap-2 text-slate-900 font-medium text-sm">
          {Icon && <Icon size={14} className="text-slate-400" />} {value || <span className="text-slate-400">—</span>}
        </div>
      )}
    </div>
  );
}

// ============================================================
// BOOKINGS
// ============================================================
function BookingsSection({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}/me/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      setRows(r.data || []);
    } catch (e) { setErr(formatApiError(e)); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <FullPageSpinner />;
  if (err) return <div className="text-sm text-red-700 bg-red-50 rounded-lg p-4">{err}</div>;
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center" data-testid="account-bookings-empty">
        <Calendar size={36} className="mx-auto text-slate-300 mb-3" />
        <div className="font-semibold text-slate-700">No bookings yet</div>
        <div className="text-sm text-slate-500 mt-1">Open the price calculator from the homepage to book your first service.</div>
        <Link to="/" className="inline-block mt-4">
          <Button className="bg-emerald-600 hover:bg-emerald-700">Browse services</Button>
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="account-bookings">
      {rows.map(b => {
        const cancelled = ['cancelled', 'client_cancelled', 'failed', 'refunded'].includes(b.status);
        const c = b.cancellation || null;
        const sh = Array.isArray(b.status_history) ? b.status_history : [];
        return (
          <div key={b.id || b._id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`booking-card-${b.id}`}>
            <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Receipt size={14} className="text-emerald-600" />
                  <span className="font-mono text-xs text-slate-500">{b.booking_code || b.id?.slice(0, 8)}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                    b.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                    cancelled ? 'bg-red-100 text-red-700' :
                    b.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                    b.status === 'on_hold' ? 'bg-amber-100 text-amber-700' :
                    b.status === 'rescheduled' ? 'bg-purple-100 text-purple-700' :
                    'bg-amber-100 text-amber-700'
                  }`} data-testid={`booking-status-${b.id}`}>{(b.status || 'pending').replace(/_/g, ' ')}</span>
                </div>
                <div className="font-semibold text-slate-900">{b.service_type || b.pest_type || b.service_label || '—'}</div>
                <div className="text-xs text-slate-500">{b.property_size || '—'} · {b.customer_city || b.city || ''}</div>
                <div className="text-xs text-slate-400 mt-1">{fmtDate(b.scheduled_date || b.created_at)}</div>
              </div>
              <div className="text-right">
                <div className={`text-lg font-bold ${cancelled ? 'text-slate-400 line-through' : 'text-emerald-700'}`}>{fmtMoney(b.final_amount || b.amount)}</div>
                {c?.refund_amount > 0 && (
                  <div className="text-[11px] text-blue-700 font-semibold mt-0.5" data-testid={`refund-tag-${b.id}`}>
                    Refunded {fmtMoney(c.refund_amount)}
                  </div>
                )}
              </div>
            </div>

            {/* Cancellation / Refund banner */}
            {cancelled && c && (
              <div className="bg-rose-50 border-t border-rose-200 px-4 py-2.5 text-xs" data-testid={`cancel-banner-${b.id}`}>
                <div className="font-semibold text-rose-800 mb-0.5">
                  ⛔ {(c.status || b.status).replace(/_/g, ' ').toUpperCase()}
                  {c.reason && <span className="font-normal"> — {c.reason}</span>}
                </div>
                {c.refund_mode && (
                  <div className="text-rose-700">
                    Refund via <b>{c.refund_mode}</b>
                    {c.razorpay_refund_id && <> · ID <span className="font-mono">{c.razorpay_refund_id}</span></>}
                    {c.refund_status && <> · Status: <b>{c.refund_status}</b></>}
                  </div>
                )}
                {c.refund_error && <div className="text-rose-700">⚠️ {c.refund_error}</div>}
                <div className="text-rose-500 mt-0.5">Cancelled by {c.by || 'admin'} on {fmtDate(c.at)}</div>
              </div>
            )}

            {/* Status Timeline */}
            {sh.length > 0 && (
              <details className="bg-slate-50 border-t border-slate-200 px-4 py-2">
                <summary className="text-[11px] font-semibold text-slate-600 cursor-pointer hover:text-emerald-700" data-testid={`timeline-toggle-${b.id}`}>
                  View status timeline ({sh.length})
                </summary>
                <ol className="mt-2 space-y-1.5" data-testid={`timeline-list-${b.id}`}>
                  {sh.slice().reverse().map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px]">
                      <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                        i === 0 ? 'bg-emerald-500 ring-2 ring-emerald-200' : 'bg-slate-400'
                      }`} />
                      <div className="flex-1">
                        <span className="font-semibold text-slate-700 uppercase">{(h.status || '').replace(/_/g, ' ')}</span>
                        {h.note && <span className="text-slate-500"> — {h.note}</span>}
                        <span className="block text-slate-400">{fmtDate(h.at)} {h.by ? `· by ${h.by}` : ''}</span>
                      </div>
                    </li>
                  ))}
                </ol>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================
// ACTIVITY
// ============================================================
function ActivitySection({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/me/activities`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (alive) setRows(r.data || []); })
      .catch(e => { if (alive) setErr(formatApiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  if (loading) return <FullPageSpinner />;
  if (err) return <div className="text-sm text-red-700 bg-red-50 rounded-lg p-4">{err}</div>;
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
        <Activity size={36} className="mx-auto text-slate-300 mb-3" />
        <div className="font-semibold text-slate-700">No activity yet</div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="account-activity">
      {rows.map(e => (
        <div key={e.id} className="px-4 py-3 flex flex-wrap items-start gap-2 text-sm">
          <span className="font-medium">{EVENT_LABELS[e.event_type] || e.event_type}</span>
          {e.path && <span className="text-xs text-slate-500">on <code className="bg-slate-100 px-1 py-0.5 rounded">{e.path}</code></span>}
          {e.payload && Object.keys(e.payload).length > 0 && (
            <span className="text-xs text-slate-400">· {Object.entries(e.payload).slice(0, 3).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(', ')}</span>
          )}
          <span className="ml-auto text-xs text-slate-400">{fmtDate(e.created_at)}</span>
        </div>
      ))}
    </div>
  );
}
